import { useEffect, useMemo, useState } from 'react';

import { ModalHeader } from '../../../UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from '../../../UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from '../../../UIPackage/ModalModule/components/ModalActions';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { Row } from '../../../UIPackage/FormModule/components/Row';
import styles from './KnmSubjectsForErvkObjectsModal.module.scss';
import { IErvkObjectsOfControl } from '../../../APIPackage/KnmModule/interfaces/IErvkObjectsOfControl';
import { IErvkObjectsOfControlSubjectData } from '../../../APIPackage/KnmModule/interfaces/IErvkObjectsOfControlSubjectData';
import { Checkbox } from '../../../UIPackage/MainModule/components/Checkbox';
import { KnmKindCodesEnum } from '../../../APIPackage/DictionariesModule/enums/KnmKindCodesEnum';

interface IKnmSubjectsForErvkObjectsModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (objects: IErvkObjectsOfControl[], subjects: IErvkObjectsOfControlSubjectData[]) => void;
  ervkObjects: IErvkObjectsOfControl[];
  kindKnmCode: string | undefined;
}

export const KnmSubjectsForErvkObjectsModal = ({
  visible,
  onClose,
  onSubmit,
  ervkObjects,
  kindKnmCode,
}: IKnmSubjectsForErvkObjectsModalProps) => {
  const [selectedSubjects, setSelectedSubjects] = useState<IErvkObjectsOfControlSubjectData[]>([]);

  useEffect(() => {
    if (visible) setSelectedSubjects([]);
  }, [visible]);

  // ИНН приоритетнее id, так как одно лицо приходит из ЕРВК разными записями с разными id.
  // Тип КЛ входит в ключ: у ФЛ и ИП может быть один ИНН, но это разные контролируемые лица.
  // id используется для КЛ без ИНН, чтобы не схлопывать разных лиц
  const subjectKey = (val: IErvkObjectsOfControlSubjectData): string => {
    if (val.inn) return `inn:${val.subjectType}:${val.inn}`;
    if (val.id) return `id:${val.id}`;

    return `data:${[val.snils, val.ogrn, val.organizationFullName, val.surName, val.firstName, val.middleName].join(
      '|',
    )}`;
  };

  const isSubjectsEquals = (
    val1: IErvkObjectsOfControlSubjectData,
    val2: IErvkObjectsOfControlSubjectData,
  ): boolean => {
    return subjectKey(val1) === subjectKey(val2);
  };

  const isSubjectSelected = (value: IErvkObjectsOfControlSubjectData): boolean => {
    return !!selectedSubjects.find(item => isSubjectsEquals(item, value));
  };
  const handleMultiSelect = (value: IErvkObjectsOfControlSubjectData) => {
    if (isSubjectSelected(value)) {
      setSelectedSubjects(selectedSubjects.filter(item => !isSubjectsEquals(item, value)));
    } else setSelectedSubjects([...selectedSubjects, value]);
  };

  const handleSelect = (value: IErvkObjectsOfControlSubjectData) => {
    setSelectedSubjects([value]);
  };

  const uniqueSubjects = (subjects: IErvkObjectsOfControlSubjectData[]) => {
    const result: IErvkObjectsOfControlSubjectData[] = [];

    subjects.forEach(x => {
      if (!result?.find(r => isSubjectsEquals(r, x))) {
        result.push(x);
      }
    });

    return result;
  };

  const subjectsData: IErvkObjectsOfControlSubjectData[] = useMemo(() => {
    return uniqueSubjects(ervkObjects.flatMap(x => x.subjects)) || [];
  }, [ervkObjects]);

  const handleSelectAll = () => {
    if (kindKnmCode === KnmKindCodesEnum.RO) setSelectedSubjects(subjectsData);
  };

  const handleDismissAll = () => {
    setSelectedSubjects([]);
  };

  const organizationInfo = (org: IErvkObjectsOfControlSubjectData) => {
    const inn = org.inn ? `ИНН: ${org.inn} ` : '';
    const ogrn = org.ogrn ? `ОГРН: ${org.ogrn} ` : '';
    const name = org.organizationFullName ? `Наименование: ${org.organizationFullName} ` : '';
    const snils = org.snils ? `СНИЛС: ${org.snils} ` : '';
    const kpp = org.kpp ? `КПП: ${org.kpp} ` : '';
    const rafp = org.rafp ? `НЗА: ${org.rafp} ` : '';
    const fio = org.firstName ? `ФИО: ${org.firstName} ${org.middleName} ${org.surName}` : '';
    const nationality = org.nationality
      ? `Государство, в котором указанное юридическое лицо зарегистрировано: ${org.nationality} `
      : '';
    const document = org.identityDocumentNumber
      ? `Документ, удостоверяющий личность: ${org.identityDocumentNumber} `
      : '';

    return inn + ogrn + name + fio + snils + kpp + rafp + nationality + document;
  };

  const handleSubmit = () => {
    onSubmit(ervkObjects, selectedSubjects);
  };

  return (
    <>
      <ModalHeader title="Выбор контролируемого лица" />
      <ModalBody overflow="scroll">
        <Row>
          <div></div>
          <div></div>
        </Row>
        <Row notSpace>
          <Button
            text="Выбрать все"
            size="medium"
            type="button"
            className={styles.LeftButton}
            disabled={kindKnmCode !== KnmKindCodesEnum.RO}
            onClick={handleSelectAll}
          />
          <Button text="Сбросить" size="medium" type="button" onClick={handleDismissAll} />
        </Row>
        <Row>
          <div className={styles.InformationSupportBody}>
            <span className={styles.InfoIcon}></span>
            <span className={styles.InformationSupportText}>
              В случае отсутствия необходимого контролируемого лица или наличия ошибок необходимо внести данные в ЕРВК.
              В паспорт будут добавлены объекты контроля в соответствии с выбранными контролированными лицами
            </span>
          </div>
        </Row>
        <div className={styles.Table}>
          <div className={styles.TableHeader}>
            <div></div>
            <div>Контролируемое лицо</div>
          </div>
          {subjectsData.length === 0 ? (
            <div className={styles.TableBodyEmpty}>
              <span>Данные отсутствуют</span>
            </div>
          ) : (
            <div className={styles.TableBody}>
              <div className={styles.TableContent}>
                {subjectsData.map((item, index) => (
                  <div
                    key={`subjectItem[${index}]`}
                    className={styles.TableRow}
                    onClick={() => (kindKnmCode === KnmKindCodesEnum.RO ? handleMultiSelect(item) : handleSelect(item))}
                  >
                    <div>
                      <Checkbox
                        theme="system"
                        checked={isSubjectSelected(item)}
                        type={kindKnmCode !== KnmKindCodesEnum.RO ? 'radio' : 'checkbox'}
                      />
                    </div>
                    <div>{organizationInfo(item)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </ModalBody>
      <ModalActions>
        <Button
          text="Добавить"
          color="primary"
          size="medium"
          onClick={handleSubmit}
          disabled={kindKnmCode === KnmKindCodesEnum.RO ? false : !selectedSubjects.length}
        />
        <Button text="Отмена" size="medium" onClick={onClose} />
      </ModalActions>
    </>
  );
};
